import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApInvoice,
  type ApInvoiceItem,
  type ApPricebookItemOption,
  type ApReceipt,
} from "@/accounts-payable/types";
import { formatDollars } from "./format";

export const MIN_PRICEBOOK_SEARCH_LENGTH = 2;

export const pricebookItemLabel = (option: ApPricebookItemOption) =>
  option.name ?? option.code ?? `item ${option.external_id}`;

export const pricebookOptionMeta = (option: ApPricebookItemOption) =>
  [
    option.code,
    option.item_type === "equipment" ? "Equipment" : "Material",
    option.cost != null ? formatDollars(Number(option.cost)) : null,
  ].filter((part): part is string => !!part);

export const hasPricebookMatch = (item: ApInvoiceItem) =>
  item.matched_pricebook_item_id != null ||
  item.matched_pricebook_item_name != null;

export const matchedPricebookItemLabel = (item: ApInvoiceItem) =>
  item.matched_pricebook_item_name ??
  (item.matched_pricebook_item_id != null
    ? `Item ${item.matched_pricebook_item_id}`
    : null);

export const pricebookProposalSku = (action: ApAgentAction): string | null => {
  if (action.action_type === AP_ACTION_TYPE.updatePricebookCost)
    return action.payload?.sku ?? null;
  if (action.action_type === AP_ACTION_TYPE.createPricebookItem)
    return action.payload?.code ?? null;
  return null;
};

export const pricebookProposalLinkLabel = (action: ApAgentAction) => {
  if (!pricebookProposalSku(action) || !action.line_item_ids?.length)
    return null;
  return action.action_type === AP_ACTION_TYPE.createPricebookItem
    ? "Use an existing item"
    : "Wrong item?";
};

export const proposalLinkAriaLabel = (label: string, subject: string) =>
  `${label.replace(/\?$/, "")}: ${subject}`;

export const documentLineItems = (document: ApInvoice | ApReceipt) =>
  document.document_type === "receipt"
    ? document.receipt_items
    : document.invoice_items;

export const pricebookSearchStatusMessage = ({
  query,
  isLoading,
  isError,
  resultCount,
}: {
  query: string;
  isLoading: boolean;
  isError: boolean;
  resultCount: number;
}): string | null => {
  if (query.trim().length < MIN_PRICEBOOK_SEARCH_LENGTH)
    return `Type at least ${MIN_PRICEBOOK_SEARCH_LENGTH} characters to search the pricebook.`;
  if (isLoading) return "Searching the pricebook…";
  if (isError) return "Couldn’t search the pricebook. Try again.";
  if (resultCount === 0) return "No pricebook items match that search.";
  return null;
};
